import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronRight, Download, BellRing, RotateCcw, AlertTriangle, ArrowUpDown } from 'lucide-react';
import GmpStatCards from '../components/gmp/GmpStatCards';
import GmpBoardTable from '../components/gmp/GmpBoardTable';
import GmpProfitCalculator from '../components/gmp/GmpProfitCalculator';
import GmpKnowledgeDesk from '../components/gmp/GmpKnowledgeDesk';
// Discussion section hidden for now
// import GmpDiscussion from '../components/gmp/GmpDiscussion';
import { DematAccountsCard, RelatedReportsCard } from '../components/gmp/GmpSidebarCards';
import { fetchGmp, selectGmpBoardRows } from '../store/slices/gmpSlice';
import { fetchSmeGmp, selectSmeGmpBoardRows } from '../store/slices/smeGmpSlice';
import {
  BOARD_FILTERS,
  SME_BOARD_FILTERS,
  SORT_OPTIONS,
  buildBoardCsv,
  computeBoardStats,
  formatRefreshedAt,
  isSmeRow,
} from '../utils/gmpBoard';
import { SME_GMP_FAQS, SME_RELATED_REPORTS } from '../data/gmpBoardData';

const PAGE_SIZE = 10;
const DATA_MAX_AGE_MS = 60 * 1000;

// Page text and scope per board: 'all' is /ipo-gmp, 'sme' is /sme-ipo-gmp
const VARIANTS = {
  all: {
    // GET /api/market/gmp (shared with the landing page's GMP section)
    feed: { fetch: fetchGmp, selectRows: selectGmpBoardRows, stateKey: 'gmp' },
    scope: () => true,
    filters: BOARD_FILTERS,
    pageTitle: 'Live IPO GMP & Listing Estimates — Alambrix IPO',
    // No middle crumb: the board sits directly under Home
    crumb: null,
    current: 'Live IPO Grey Market Premium (GMP)',
    heading: 'Live IPO Grey Market Premium (GMP) & Listing Estimates',
    description:
      'Unofficial grey market premiums, day ranges, subscription levels and computed listing expectations for ongoing and upcoming Indian Mainboard and SME IPOs.',
    exportName: 'ipo-gmp-board',
    alertText: "GMP price alerts are coming soon. We'll notify you here once they're available.",
    board: {},
    knowledge: {},
    reports: {},
    discussion: null,
    crossLink: null,
    disclaimerNote: null,
  },
  sme: {
    // GET /api/market/gmp/sme -- SME issues only; the board filter stays as a safety net
    feed: { fetch: fetchSmeGmp, selectRows: selectSmeGmpBoardRows, stateKey: 'smeGmp' },
    scope: isSmeRow,
    filters: SME_BOARD_FILTERS,
    pageTitle: 'Live SME IPO GMP & Listing Estimates — Alambrix IPO',
    crumb: { label: 'SME IPOs', to: '/sme-ipos' },
    current: 'Live SME IPO Grey Market Premium (GMP)',
    heading: 'Live SME IPO Grey Market Premium (GMP) & Listing Estimates',
    description:
      'Unofficial grey market premiums, day ranges, subscription levels and computed listing expectations for SME IPOs on NSE Emerge and BSE SME. SME quotes come from a thin dealer market, so they can move sharply between updates.',
    exportName: 'sme-ipo-gmp-board',
    alertText: "SME GMP alerts are coming soon. We'll notify you here once they're available.",
    board: { title: 'Current SME IPO Grey Market Premium Board', noun: 'SME IPOs' },
    knowledge: {
      title: 'SME IPO GMP & Kostak Knowledge Desk',
      subtitle: 'How to read grey market quotes for SME issues, where every application is a large lot.',
      example:
        'Example: an SME issue priced at ₹78 with a GMP of ₹12 points to listing near ₹90 per share (+15.4%). On a 1,600-share lot that is about ₹19,200 per lot before charges.',
      faqs: SME_GMP_FAQS,
    },
    reports: { title: 'Related SME Reports', reports: SME_RELATED_REPORTS },
    discussion: {
      headingId: 'sme-gmp-discussion-heading',
      title: 'SME Investor Community & Live GMP Discussion',
      subtitle: 'Questions on SME lot sizes, allotment odds and listing-day strategy from fellow applicants.',
      placeholder: 'Join the discussion… Ask about SME GMP trends, lot sizes or listing-day strategy',
    },
    crossLink: { label: 'All IPO GMP', to: '/ipo-gmp' },
    disclaimerNote:
      ' SME quotes are especially thin: SME shares trade in large lots with limited post-listing liquidity, so a quoted premium can change sharply or disappear.',
  },
};

const SyncBadge = ({ status, hasData, refreshedLabel }) => {
  let tone = 'bg-emerald-50 border-emerald-200 text-emerald-700';
  let dot = 'bg-emerald-500 animate-pulse';
  let text = refreshedLabel ? `Live Market Data · Updated ${refreshedLabel}` : 'Live Market Data';

  if (status === 'loading' || status === 'idle') {
    tone = 'bg-slate-50 border-slate-200 text-slate-600';
    dot = 'bg-slate-400 animate-pulse';
    text = hasData ? 'Refreshing live data…' : 'Connecting to live data…';
  } else if (status === 'failed') {
    tone = 'bg-rose-50 border-rose-200 text-rose-600';
    dot = 'bg-rose-500';
    text = hasData ? `Refresh failed · Showing data from ${refreshedLabel || 'last sync'}` : 'Live data unavailable';
  }

  return (
    <span className={`inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full border text-[11px] font-bold ${tone}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {text}
    </span>
  );
};

export const GmpPage = ({ variant = 'all' }) => {
  const config = VARIANTS[variant] || VARIANTS.all;
  const { filters } = config;
  const dispatch = useDispatch();
  const { feed } = config;
  const allRows = useSelector(feed.selectRows);
  const rows = useMemo(() => allRows.filter(config.scope), [allRows, config]);
  const { status, error, refreshedAt, source } = useSelector((state) => state[feed.stateKey]);

  const [searchParams, setSearchParams] = useSearchParams();
  const requestedFilter = searchParams.get('filter');
  const activeFilter = filters.find((f) => f.key === requestedFilter) || filters[0];

  const [sortKey, setSortKey] = useState(SORT_OPTIONS[0].key);
  const [page, setPage] = useState(1);
  const [alertNotice, setAlertNotice] = useState(false);

  const hasData = rows.length > 0;
  const isInitialLoading = !hasData && (status === 'idle' || status === 'loading');
  const loadError = !hasData && status === 'failed' ? error : null;
  const refreshedLabel = formatRefreshedAt(refreshedAt);

  useEffect(() => {
    dispatch(feed.fetch({ maxAgeMs: DATA_MAX_AGE_MS }));
  }, [dispatch, feed]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = config.pageTitle;
    return () => {
      document.title = previousTitle;
    };
  }, [config]);

  useEffect(() => {
    setPage(1);
  }, [activeFilter.key, sortKey]);

  useEffect(() => {
    if (!alertNotice) return undefined;
    const timer = setTimeout(() => setAlertNotice(false), 3500);
    return () => clearTimeout(timer);
  }, [alertNotice]);

  const stats = useMemo(() => computeBoardStats(rows), [rows]);
  const filterCounts = useMemo(
    () => Object.fromEntries(filters.map((f) => [f.key, rows.filter(f.test).length])),
    [rows, filters]
  );
  const calculatorRows = useMemo(
    () =>
      rows.filter((row) => row.hasGmp && row.price !== null && row.lotSize !== null && !row.isClosed),
    [rows]
  );

  const visibleRows = useMemo(() => {
    const sort = SORT_OPTIONS.find((s) => s.key === sortKey) || SORT_OPTIONS[0];
    return rows.filter(activeFilter.test).sort(sort.compare);
  }, [rows, activeFilter, sortKey]);

  // Clamp in case a refresh returns fewer rows than the current page needs
  const totalPages = Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = visibleRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const setFilter = (key) => {
    setSearchParams(key === 'all' ? {} : { filter: key }, { replace: true });
  };

  const handlePageChange = (next) => {
    if (next < 1 || next > totalPages || next === currentPage) return;
    setPage(next);
    document.getElementById('gmp-board')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleRefresh = () => {
    dispatch(feed.fetch());
  };

  const handleReset = () => {
    setFilter('all');
    setSortKey(SORT_OPTIONS[0].key);
  };

  const handleExport = () => {
    // BOM so spreadsheet apps read the file as UTF-8
    const blob = new Blob(['﻿', buildBoardCsv(visibleRows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${config.exportName}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <main className="flex-1 bg-slate-50/70">
      <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28 pt-4 sm:pt-6 pb-12 sm:pb-16">
        {/* Breadcrumb */}
        <nav
          className="flex items-center gap-1.5 text-xs text-slate-500 font-medium mb-4 overflow-x-auto whitespace-nowrap"
          aria-label="Breadcrumb"
        >
          <Link to="/" className="hover:text-[#008080] transition-colors">
            Home
          </Link>
          {config?.crumb && (
            <>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <Link to={config.crumb.to} className="hover:text-[#008080] transition-colors">
                {config.crumb.label}
              </Link>
            </>
          )}
          <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span className="text-[#008080] font-semibold">{config.current}</span>
        </nav>

        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-5 sm:mb-6">
          <div className="max-w-3xl">
            <h1 className="text-2xl sm:text-3xl lg:text-[34px] font-extrabold text-slate-900 tracking-tight leading-tight">
              {config.heading}
            </h1>
            <SyncBadge status={status} hasData={hasData} refreshedLabel={refreshedLabel} />
            <p className="text-slate-500 text-xs sm:text-sm mt-2 leading-relaxed">{config.description}</p>
          </div>

          <div className="relative flex flex-wrap items-center gap-2">
            {config.crossLink && (
              <Link
                to={config.crossLink.to}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:border-slate-300 hover:bg-slate-50 transition-colors shadow-sm"
              >
                {config.crossLink.label}
              </Link>
            )}
            <button
              type="button"
              onClick={handleExport}
              disabled={visibleRows.length === 0}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              <Download className="w-3.5 h-3.5" />
              Export Sheet (CSV)
            </button>
            {/* <button
              type="button"
              onClick={() => setAlertNotice(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-teal-200 bg-teal-50 text-xs font-bold text-[#008080] hover:bg-[#008080] hover:text-white transition-colors shadow-sm"
            >
              <BellRing className="w-3.5 h-3.5" />
              Set GMP Price Alert
            </button> */}
            {alertNotice && (
              <div
                role="status"
                className="absolute right-0 top-full mt-2 z-20 w-64 rounded-xl bg-slate-900 text-white text-xs px-3 py-2.5 shadow-lg"
              >
                {config.alertText}
              </div>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <GmpStatCards stats={stats} loading={isInitialLoading} variant={variant} />

        {/* Filter Chips & Sort */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 mt-5 sm:mt-6 mb-4">
          <div className="flex flex-wrap items-center gap-2 flex-1" role="group" aria-label="Filter IPOs">
            {filters.map((filter) => {
              const isActive = activeFilter.key === filter.key;
              return (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => setFilter(filter.key)}
                  aria-pressed={isActive}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${isActive
                    ? 'bg-[#008080] border-[#008080] text-white'
                    : filter.urgent
                      ? 'bg-white border-rose-200 text-rose-600 hover:bg-rose-50'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-900'
                    }`}
                >
                  {filter.urgent && !isActive && <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />}
                  {filter.label}
                  <span
                    className={`min-w-[20px] px-1.5 py-px rounded-full text-[10px] text-center ${isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                      }`}
                  >
                    {filterCounts[filter.key]}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <label htmlFor="gmp-sort" className="flex items-center gap-1 text-xs font-semibold text-slate-500 whitespace-nowrap">
              <ArrowUpDown className="w-3.5 h-3.5" />
              Sort By:
            </label>
            <select
              id="gmp-sort"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
              className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-700 focus:outline-none focus:border-[#008080] focus:ring-1 focus:ring-[#008080]"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleReset}
              title="Reset filter and sort"
              aria-label="Reset filter and sort"
              className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-[#008080] hover:border-teal-200 transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Main Grid: Board + Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] xl:grid-cols-[minmax(0,1fr)_320px] gap-5 sm:gap-6 items-start">
          <div className="space-y-5 sm:space-y-6 min-w-0">
            <div id="gmp-board" className="scroll-mt-20">
              <GmpBoardTable
                rows={pageRows}
                totalRecords={rows.length}
                filteredCount={visibleRows.length}
                page={currentPage}
                pageSize={PAGE_SIZE}
                onPageChange={handlePageChange}
                isLoading={isInitialLoading}
                error={loadError}
                isRefreshing={status === 'loading'}
                onRefresh={handleRefresh}
                refreshedLabel={refreshedLabel}
                sourceLabel={source}
                {...config.board}
              />
            </div>
            <GmpKnowledgeDesk {...config.knowledge} />
            {/* Discussion section hidden for now: {config.discussion ? <GmpDiscussion initialThreads={[]} copy={config.discussion} /> : <GmpDiscussion />} */}

          </div>

          <aside className="space-y-5 sm:space-y-6">
            {/* Re-initialise the calculator whenever fresh data arrives */}
            <GmpProfitCalculator key={refreshedAt || 'pending'} rows={calculatorRows} loading={isInitialLoading} />
            <DematAccountsCard />
            <RelatedReportsCard {...config.reports} />
          </aside>
        </div>

        {/* Statutory Disclaimer */}
        <div className="mt-8 sm:mt-10 rounded-2xl border border-amber-200 bg-amber-50/70 p-4 sm:p-6">
          <h2 className="flex items-center gap-2 text-xs sm:text-sm font-extrabold text-amber-800 uppercase tracking-wide">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            Statutory Disclaimer &amp; Risk Notice Regarding Grey Market Premium (GMP)
          </h2>
          <p className="text-[11px] sm:text-xs text-amber-900/80 leading-relaxed mt-2">
            Grey Market Premium (GMP) figures shown on Alambrix are unofficial indicative quotes collected from third-party
            market sources for educational and statistical purposes only. The grey market is not recognised, approved or
            regulated by SEBI, BSE, NSE or any other statutory authority, and there are no enforceable contracts behind
            these quotes. Past GMP does not guarantee listing-day performance. Investors should read the Draft and Red
            Herring Prospectus (DRHP/RHP) before applying. Alambrix does not act as a broker, execute trades or promote
            grey-market transactions.
            {config.disclaimerNote}
          </p>
        </div>
      </div>
    </main>
  );
};

export default GmpPage;
